/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, FileDown, Calendar as CalendarIcon, Users, AlertTriangle, Clock, Share2, Mail, Download, Award, Loader2, FileWarning, History as HistoryIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { generateIdempotencyKey } from "@/lib/idempotency";
import {
  STAGE_LABELS,
  describeExportError,
  formatBytes,
  SECTIONS,
  DEFAULT_ON_SECTIONS,
  todayISO,
  daysAgoISO,
  runAsyncCourtBundleJob,
  retryCourtBundleJob,
} from "@/lib/courtBundleHelpers";

// v77.99.77 — `STAGE_LABELS`, `describeExportError`, `formatBytes`,
// `SECTIONS`, `DEFAULT_ON_SECTIONS`, `todayISO`, `daysAgoISO`, and the
// `runAsyncCourtBundleJob` async-job driver moved to
// `lib/courtBundleHelpers.js`. All pure / self-contained — they take
// everything they need via arguments.

// v77.99.29 — Stage label dictionary moved to lib/courtBundleHelpers.js
// (along with the rest of the export helpers + the async-job driver).

export default function CourtBundle() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkChildId = searchParams.get("child_id");
  // iter 126.cb-relationship — When the user lands here via the
  // Court Orders "Export bundle" jump, `?relationship_id=` narrows
  // the export scope + is stamped into the fingerprint.
  const deepLinkRelationshipId = searchParams.get("relationship_id");
  const [children, setChildren] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [relationshipId, setRelationshipId] = useState(deepLinkRelationshipId || "");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // v77.99.29 — Rich progress state for the export flow. Holds the
  // active job's stage label, % complete, current child's name (for
  // the per-child loop), and any final failure detail. Used to render
  // the progress card and the friendly error panel.
  const [progress, setProgress] = useState(null);
  // Last-failure detail panel. Kept around so the form survives the
  // failure (form state is in React state already; this is just the
  // explanatory copy + suggested next steps).
  const [lastFailure, setLastFailure] = useState(null);
  // Pre-flight estimate gate. When non-null, the export button has
  // been pressed AND we need confirmation before kicking off the
  // job (size warning or recommendations available).
  const [pendingEstimate, setPendingEstimate] = useState(null);

  // v77.99.98 — "Email me when ready" toggle. When ON, the export
  // helper sends a `batch_id` + `X-Notify-Email` header so the
  // backend groups every sibling render (one per child) and fires a
  // SINGLE combined email when the whole batch settles. The user can
  // close the tab safely; downloads remain available from the
  // 30-day history page.
  const [notifyEmailOnReady, setNotifyEmailOnReady] = useState(() => {
    try { return localStorage.getItem("cpc.courtBundle.notifyEmail") === "1"; }
    catch { return false; }
  });
  const persistNotifyEmail = (v) => {
    setNotifyEmailOnReady(v);
    try {
      if (v) localStorage.setItem("cpc.courtBundle.notifyEmail", "1");
      else localStorage.removeItem("cpc.courtBundle.notifyEmail");
    } catch { /* ignore */ }
  };

  // Section selection — every section enabled by default EXCEPT the
  // audit log (see DEFAULT_ON_SECTIONS above for rationale).
  const [selectedSections, setSelectedSections] = useState(
    Object.fromEntries(SECTIONS.map((s) => [s.key, DEFAULT_ON_SECTIONS.has(s.key)])),
  );

  // Iter 122.cb-cv — Per-section Contact Visits options. The defaults
  // mirror the backend `ContactVisitsBundleOptions` Pydantic model —
  // every evidentially-meaningful field on, raw audit timeline and
  // private notes off (parents opt those in when they specifically
  // need a court-grade record).
  const [cvOptions, setCvOptions] = useState({
    mode: "all",            // "all" | "filter"  ("specific" is reserved for a later iteration)
    status_filter: null,
    visit_type_filter: null,
    child_id_filter: null,
    include_times:         true,
    include_duration:      true,
    include_venue:         true,
    include_gps:           true,
    include_participants:  true,
    include_outcome:       true,
    include_verification:  true,
    include_timeline:      false,
    include_notes:         true,
    include_private_notes: false,
    include_attachments:   true,
    include_observations:  true,
    summary_only:          false,
  });
  const setCvOption = (k, v) => setCvOptions((s) => ({ ...s, [k]: v }));

  // Date range — default: last 30 days.
  const [startDate, setStartDate] = useState(daysAgoISO(30));
  const [endDate, setEndDate] = useState(todayISO());

  // v77.37 — Default to "One PDF per child" since solicitors / mediators
  // typically file per-child anyway, and the rest of the app (Messages,
  // Documents) now strictly enforces per-child threading. Combined
  // remains available as a secondary option for an overall picture.
  const [scopeMode, setScopeMode] = useState("per_child");
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  // v77.38 — Last bundle exported per child (read-only summary) so the
  // primary parent / mediator can tell at a glance which child's
  // records have been kept current. Refreshes whenever a new bundle
  // is generated.
  const [recentByChild, setRecentByChild] = useState({});
  // v77.48 — In-session bundle handles (one entry per PDF generated
  // this session). Drives the Share section: re-download, native
  // Web Share API (iOS / Android pick: Email, Drive, WhatsApp, etc.),
  // and a solicitor-email mailto fallback. We don't persist the
  // blobs across reloads — they're memory-only for security; users
  // can always regenerate.
  const [generatedBundles, setGeneratedBundles] = useState([]);
  // v77.99.97 — Per-child failure status map keyed by target label.
  // Survives partial failures so the user sees which children
  // succeeded vs. failed, and can hit "Retry" on the failed ones
  // without re-running the whole batch.
  // Shape: { [label]: { status: "queued"|"preparing"|"ready"|"failed",
  //                     child_ids, error, target_idx } }
  const [bundleStatus, setBundleStatus] = useState({});
  const [solicitorEmail, setSolicitorEmail] = useState(() => {
    try { return localStorage.getItem("cpc.courtBundle.solicitorEmail") || ""; }
    catch { return ""; }
  });
  const persistSolicitorEmail = (v) => {
    setSolicitorEmail(v);
    try {
      if (v) localStorage.setItem("cpc.courtBundle.solicitorEmail", v);
      else localStorage.removeItem("cpc.courtBundle.solicitorEmail");
    } catch { /* ignore */ }
  };
  // v77.48 — Detect Web Share API file support once. Older mobile
  // browsers expose `navigator.share` for plain text but reject
  // file payloads — `canShare({files})` is the strict probe.
  const [canShareFiles, setCanShareFiles] = useState(false);
  useEffect(() => {
    try {
      // probe with a 1-byte test file so we know `files` is supported
      const probe = new File([new Blob([""], { type: "application/pdf" })], "probe.pdf", { type: "application/pdf" });
      setCanShareFiles(
        typeof navigator !== "undefined"
        && typeof navigator.share === "function"
        && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [probe] }),
      );
    } catch {
      setCanShareFiles(false);
    }
  }, []);
  const loadRecent = () => {
    api.get("/exports/court-bundle/recent")
      .then((r) => {
        const map = {};
        (r.data?.per_child || []).forEach((row) => { map[row.child_id] = row; });
        setRecentByChild(map);
      })
      .catch(() => setRecentByChild({}));
  };
  useEffect(() => { loadRecent(); }, []);

  // iter 126.cb-relationship — Fetch relationships once so the user
  // can scope the bundle to a specific parent pair. Non-fatal if the
  // fetch fails; the selector simply hides.
  useEffect(() => {
    api.get("/relationships")
      .then((r) => setRelationships(r.data || []))
      .catch(() => setRelationships([]));
  }, []);

  useEffect(() => {
    api.get("/children?include_archived=false")
      .then((r) => {
        const list = r.data || [];
        setChildren(list);
        // v77.40 — Honour ?child_id= deep-link from the dashboard
        // "Stale records" banner so the user lands with that child
        // already pre-selected and the export is one click away.
        if (deepLinkChildId && list.some((c) => c.id === deepLinkChildId)) {
          setSelectedChildIds([deepLinkChildId]);
        } else {
          // v77.52 — No children selected by default. The user MUST
          // explicitly pick which child(ren) the bundle covers — court
          // bundles can carry sensitive content for the wrong child if
          // we silently include everyone. The export button stays
          // disabled until at least one child is picked.
          setSelectedChildIds([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [deepLinkChildId]);

  const allSelected = useMemo(
    () => SECTIONS.every((s) => selectedSections[s.key]),
    [selectedSections],
  );

  const noneSelected = useMemo(
    () => SECTIONS.every((s) => !selectedSections[s.key]),
    [selectedSections],
  );

  const toggleSection = (key) =>
    setSelectedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAllSections = (val) =>
    setSelectedSections(Object.fromEntries(SECTIONS.map((s) => [s.key, val])));

  // Iter 122.cb-cv — Live Bundle Preview. We poll
  // `/exports/court-bundle/estimate` whenever the date range, section
  // selection or child selection changes so the user sees
  // "18 Contact Visits · 7 observations · 42 messages …" before they
  // click Generate. Debounced to 350ms because changing dates rapidly
  // toggling sections would otherwise hammer the endpoint.
  const [preview, setPreview] = useState(null);     // { counts, est_total_pages, ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) { setPreview(null); return; }
    if (selectedChildIds.length === 0) { setPreview(null); return; }
    const sectionsCsv = SECTIONS.filter((s) => selectedSections[s.key]).map((s) => s.key).join(",");
    if (!sectionsCsv) { setPreview(null); return; }
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date:   endDate,
          child_ids:  selectedChildIds.join(","),
          sections:   sectionsCsv,
        });
        if (relationshipId) params.set("relationship_id", relationshipId);
        const r = await api.get(`/exports/court-bundle/estimate?${params.toString()}`);
        setPreview(r.data);
      } catch { setPreview(null); }
      finally { setPreviewLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [startDate, endDate, selectedChildIds, selectedSections, relationshipId]);

  const toggleChild = (cid) =>
    setSelectedChildIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid],
    );

  // Validate the form before export. Returns null if OK or a toast-ready message.
  const validate = () => {
    if (!startDate || !endDate) return "Pick a start and end date.";
    if (startDate > endDate) return "Start date must be before end date.";
    if (noneSelected) return "Choose at least one section to include.";
    if (selectedChildIds.length === 0) return "Please select at least one child before exporting your court bundle.";
    return null;
  };

  // v77.99.29 — Pre-flight estimate helper. Returns the server's
  // estimate payload (counts, warning_level, recommendations). We
  // don't block on it (no spinner) — it's a quick GET and the user
  // only sees a confirmation step if the bundle is likely to be huge.
  const fetchEstimate = async ({ child_ids, sections }) => {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        child_ids: child_ids.join(","),
        sections: sections.join(","),
      });
      if (relationshipId) params.set("relationship_id", relationshipId);
      const r = await api.get(`/exports/court-bundle/estimate?${params.toString()}`);
      return r.data;
    } catch (e) {
      // If the estimate endpoint fails we just skip the warning and
      // attempt the export — don't block the user on a non-essential
      // pre-flight call.
      return null;
    }
  };

  // Trigger the actual export. For scopeMode === 'per_child' we call
  // the endpoint once per selected child so the user gets one PDF per
  // child. For 'all' we send every selected child in a single
  // request and download a combined bundle.
  //
  // v77.99.29 — `overrides` lets the recommendation modal trigger a
  // safer-shape rerun without resetting the user's form. Shape:
  //   { scopeMode?, sections?, skipEstimate? }
  const onExport = async (overrides = {}) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const sections = (overrides.sections
      ? SECTIONS.filter((s) => overrides.sections.includes(s.key)).map((s) => s.key)
      : SECTIONS.filter((s) => selectedSections[s.key]).map((s) => s.key));
    const effectiveScopeMode = overrides.scopeMode || scopeMode;

    // Clear any previous failure card before we start.
    setLastFailure(null);
    setPendingEstimate(null);

    // v77.99.29 — Pre-flight estimate ONLY for combined exports that
    // span 2+ children. Per-child exports are already split (each PDF
    // is smaller) so the estimate would be noise. Single-child
    // combined is the same as per-child.
    if (
      !overrides.skipEstimate
      && effectiveScopeMode === "all"
      && selectedChildIds.length > 1
    ) {
      const est = await fetchEstimate({ child_ids: selectedChildIds, sections });
      if (est && (est.warning_level === "high" || (est.warning_level === "warn" && est.recommendations.length > 0))) {
        // Show the confirmation step rather than starting immediately.
        setPendingEstimate({ estimate: est, sections });
        return;
      }
    }

    setExporting(true);
    setProgress(null);
    // v77.99.98 — Generate a single batch_id for this entire export
    // run so every per-child sibling job is grouped server-side under
    // the same batch. When `notifyEmailOnReady` is on the backend
    // fires ONE combined email when the last sibling settles.
    const batchId = generateIdempotencyKey();
    try {
      const targets =
        effectiveScopeMode === "per_child"
          ? selectedChildIds.map((cid) => ({ child_ids: [cid], label: children.find((c) => c.id === cid)?.name || cid }))
          : [{ child_ids: selectedChildIds, label: "all-children" }];

      const fresh = [];
      // v77.99.97 — Seed the status map so every target shows up as
      // "queued" before the first call. If a single iteration throws
      // (network blip / gateway timeout / browser tab throttle), we
      // record "failed" + the error and KEEP GOING — the previous
      // implementation aborted the loop on the first failure, which
      // meant a transient hiccup on Child 3 of 3 would silently kill
      // the run for ALL remaining children.
      const initialStatus = {};
      targets.forEach((t, i) => {
        initialStatus[t.label] = {
          status: "queued",
          child_ids: t.child_ids,
          target_idx: i + 1,
        };
      });
      setBundleStatus(initialStatus);
      let targetIdx = 0;
      const failures = [];
      for (const t of targets) {
        targetIdx += 1;
        setBundleStatus((s) => ({
          ...s,
          [t.label]: { ...s[t.label], status: "preparing" },
        }));
        try {
          const fp = await runAsyncCourtBundleJob({
            start_date: startDate,
            end_date: endDate,
            child_ids: t.child_ids,
            sections,
            label: t.label,
            batch_id: batchId,
            // v77.99.160 (iter 138.cb-batch-gate) — Declare the
            // full batch size up front so the backend only fires
            // the "ready" email after every expected child has
            // reached a terminal state. Without this, a serialized
            // per-child submission (POST child 1 → poll → POST
            // child 2) was triggering the email the moment child 1
            // completed, when the batch still had just 1 of N
            // members.
            batch_expected_count: targets.length,
            notify_email_on_ready: notifyEmailOnReady,
            relationship_id: relationshipId || undefined,
          // Iter 122.cb-cv hang-fix — only attach the options block
          // when the section is actually enabled in this generation
          // pass. Sending `null` was tolerated by the backend but
          // muddied the API contract and made test fixtures harder.
          ...(selectedSections.contact_visits ? { contact_visits_options: cvOptions } : {}),
            onProgress: (p) => setProgress({
              ...p,
              target_idx: targetIdx,
              target_count: targets.length,
              target_label: t.label,
            }),
          });
          fresh.push({
            id: `${fp.filename}-${Date.now()}`,
            label: t.label,
            filename: fp.filename,
            blob: fp.blob,
            fingerprint: fp.fingerprint,
            generated_at: new Date().toISOString(),
          });
          setBundleStatus((s) => ({
            ...s,
            [t.label]: { ...s[t.label], status: "ready", fingerprint: fp.fingerprint },
          }));
        } catch (err) {
          const errMsg = err?.response?.data?.detail || err?.message
            || "Connection dropped while preparing your bundle.";
          failures.push({
            label: t.label,
            child_ids: t.child_ids,
            error: errMsg,
            // v77.99.157 — surface the failed job_id (helper attaches
            // it) so the all-failed branch below can offer Retry.
            failed_job_id: err?.job_id || null,
          });
          setBundleStatus((s) => ({
            ...s,
            [t.label]: { ...s[t.label], status: "failed", error: errMsg },
          }));
          // Keep iterating — the next child's bundle should still try.
        }
      }
      // Replace the in-session bundle list with this batch so the
      // Share section reflects exactly what the user just produced
      // (rather than accumulating stale handles across regenerations).
      // We do this BEFORE the user-facing toast so the page state and
      // the toast can never disagree.
      setGeneratedBundles(fresh);
      // v77.99.115m — Single, mutually-exclusive outcome toast.
      // Previously we fired the "Generated N bundle(s)" / "Failed"
      // toast here AND a separate "Court bundle downloaded" toast a
      // few lines down — which meant the user saw "Court bundle
      // downloaded" stacked on top of "None of the bundles could be
      // generated" when every target failed (see screenshot bug
      // report Jun 2026). We now consolidate into ONE toast per
      // outcome AND surface the real backend error string in the
      // partial / total-fail branches so the user (and support) can
      // actually diagnose the failure rather than reading a generic
      // "pick fewer children" hint that doesn't apply to a
      // single-child run.
      if (failures.length === 0) {
        toast.success(
          targets.length === 1
            ? "Court bundle downloaded."
            : `${fresh.length} court bundles downloaded (one per child).`,
        );
      } else if (fresh.length > 0) {
        const failedLabels = failures.map((f) => f.label).join(", ");
        toast.error(
          `${fresh.length} bundle${fresh.length === 1 ? "" : "s"} ready, ${failures.length} failed.`,
          {
            description: `${failedLabels} — ${failures[0].error}. Click Generate again to retry the failed children.`,
            duration: 10000,
          },
        );
      } else {
        // Surface the ACTUAL backend error rather than a misleading
        // "pick fewer children" hint that confuses single-child runs.
        const detail = failures[0]?.error || "Unknown error from the export service.";
        toast.error("Couldn't generate court bundle.", {
          description: detail,
          duration: 12000,
        });
        // Render the persistent failure card too so the user has
        // somewhere to read the error after the toast disappears.
        // v77.99.157 (iter 135.cb-stall) — Prefer Retry copy; never
        // blame the user's date range for a legitimate export.
        setLastFailure({
          headline: "We couldn't complete this Court Bundle.",
          detail: `${detail}. Your records and selections are safe.`,
          next_steps: [
            "Tap Retry this export to run the same frozen snapshot again.",
            "If retry keeps failing, send a screenshot of this error to support.",
          ],
          raw: detail,
          at: new Date().toISOString(),
          failed_job_id: failures[0]?.failed_job_id || null,
        });
      }
      // v77.48 — First-time achievement nudge. No-ops on subsequent
      // bundles per user. Self-suppresses via localStorage. Only fire
      // when at least one bundle actually succeeded — celebrating a
      // run where everything failed would be insulting.
      if (fresh.length > 0) {
        celebrate({
          key: "first-court-bundle",
          userId: user?.id,
          title: "First court bundle generated",
          body: "Nicely done — your records are now court-ready. Each PDF is fingerprinted with SHA-256 and a QR code so any solicitor can verify it without an account.",
          icon: Award,
        });
      }
      // v77.49 — Court bundle generation by a third-party viewer is
      // strong evidence in any later proceedings ("yes, my solicitor
      // saw the records on this date"). Record one event per bundle
      // so the granter has a verifiable trail.
      for (const f of fresh) {
        recordThirdPartyView({
          resource_type: "court_bundle",
          resource_id: f.fingerprint,
          resource_label: `Generated bundle · ${f.label} · ${startDate} → ${endDate}`,
        });
      }
      loadRecent();
    } catch (e) {
      // v77.99.29 — Friendly error card. Form state is already
      // preserved (React state). Toast shows the headline, the
      // explanatory card under the export button has the detail +
      // recommended next steps.
      // v77.99.157 (iter 135.cb-stall) — Capture the failed job_id
      // when the helper attached it, so the failure card can offer
      // "Retry this export" against the original frozen snapshot.
      const desc = describeExportError(e, { multiChild: selectedChildIds.length > 1 });
      setLastFailure({
        ...desc,
        raw: e?.message || String(e),
        at: new Date().toISOString(),
        failed_job_id: e?.job_id || null,
      });
      toast.error(desc.headline);
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  // v77.99.29 — Run-again helper for the recommendation modal +
  // failure card "Try as one PDF per child" / "Without attachments"
  // shortcuts. Reuses the main onExport with overrides so we never
  // duplicate the loop body.
  const runWithRecommendation = (recoId) => {
    if (recoId === "split_per_child") {
      setScopeMode("per_child");
      onExport({ scopeMode: "per_child", skipEstimate: true });
    } else if (recoId === "drop_attachments") {
      // Drop the heavy sections — documents + audit log.
      const next = { ...selectedSections, documents: false, audit: false };
      setSelectedSections(next);
      const sections = SECTIONS.filter((s) => next[s.key]).map((s) => s.key);
      onExport({ sections, skipEstimate: true });
    } else if (recoId === "shorten_range") {
      // Switch to 90-day window from end_date.
      const end = new Date(endDate);
      const start = new Date(end);
      start.setDate(start.getDate() - 90);
      setStartDate(start.toISOString().slice(0, 10));
      // Don't auto-run; let the user inspect the new range and
      // tap Export.
      setPendingEstimate(null);
      toast.info("Date range shortened to the last 90 days. Tap Export when ready.");
    }
  };

  // v77.99.157 (iter 135.cb-stall) — Retry a previously failed
  // bundle against its ORIGINAL frozen snapshot. The server persisted
  // the request body at enqueue time, so this preserves the exact
  // same range, children, sections, relationship and
  // contact_visits_options — no client-side reconstruction needed.
  // The original failed job stays in History; the retry is linked
  // via `retry_of` / `retried_as` for a complete audit trail.
  const retryFailedBundle = async () => {
    const failedJobId = lastFailure?.failed_job_id;
    if (!failedJobId) {
      toast.error("Retry unavailable — please regenerate this bundle from the form above.");
      return;
    }
    setLastFailure(null);
    setExporting(true);
    setProgress({ stage: "queued", progress_pct: 2, label: "Retry of failed export" });
    try {
      const fp = await retryCourtBundleJob({
        failed_job_id: failedJobId,
        label: "retry",
        onProgress: (p) => setProgress({
          ...p,
          target_idx: 1,
          target_count: 1,
          target_label: "Retry of failed export",
        }),
      });
      setGeneratedBundles([{
        id: `${fp.filename}-${Date.now()}`,
        label: "retry",
        filename: fp.filename,
        blob: fp.blob,
        fingerprint: fp.fingerprint,
        generated_at: new Date().toISOString(),
      }]);
      toast.success("Court bundle retry completed.");
      loadRecent();
    } catch (e) {
      const desc = describeExportError(e, { multiChild: selectedChildIds.length > 1 });
      setLastFailure({
        ...desc,
        raw: e?.message || String(e),
        at: new Date().toISOString(),
        failed_job_id: e?.job_id || failedJobId,
      });
      toast.error(desc.headline);
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  // v77.48 — Share helpers. Three paths:
  //   1. Native Web Share API with files (iOS Safari, Android Chrome) →
  //      one tap brings up the OS share sheet (Email / Drive / WhatsApp /
  //      AirDrop / Messages / etc.).
  //   2. Solicitor email mailto: → opens the user's default mail
  //      client with a sensible subject + body. Mailto can't attach
  //      files, but the body includes the filename hint and the
  //      verification fingerprint so the recipient knows what to
  //      expect when the user attaches the file they downloaded.
  //   3. Re-download → for users who lost the file mid-share.
  const reDownload = (b) => {
    const url = URL.createObjectURL(b.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = b.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const shareNative = async (bundles) => {
    try {
      const files = bundles.map(
        (b) => new File([b.blob], b.filename, { type: "application/pdf" }),
      );
      // Defensive: re-check `canShare(files)` because the supported
      // size / count can vary by device.
      if (!navigator.canShare?.({ files })) {
        toast.error("Your browser can't share these files. Use email or re-download instead.");
        return;
      }
      await navigator.share({
        files,
        title: bundles.length === 1 ? `Court bundle — ${bundles[0].label}` : "Court bundles",
        text: `Court bundle generated ${startDate} → ${endDate}. Verified via CoParent Console.`,
      });
      // Some browsers don't reject when the user cancels — that's fine,
      // no toast spam either way.
    } catch (e) {
      // User cancelled is `AbortError` — silent. Real failures we surface.
      if (e?.name && e.name !== "AbortError") {
        toast.error(e.message || "Couldn't open the share sheet");
      }
    }
  };

  const emailToSolicitor = (bundles) => {
    const to = (solicitorEmail || "").trim();
    const filenames = bundles.map((b) => b.filename).join("\n  • ");
    const subject = `Court bundle: ${startDate} → ${endDate}`;
    const body = [
      "Hi,",
      "",
      `Please find the attached court bundle${bundles.length === 1 ? "" : "s"}, generated through CoParent Console for the period ${startDate} → ${endDate}.`,
      "",
      `File${bundles.length === 1 ? "" : "s"}:`,
      `  • ${filenames}`,
      "",
      "Each PDF includes a SHA-256 fingerprint and a QR code so you can verify the bundle's authenticity at a glance — no account needed on the recipient side.",
      "",
      "Note: please attach the PDF(s) you just downloaded — most email clients won't pick them up automatically.",
      "",
      "Thanks,",
    ].join("\n");
    const params = new URLSearchParams({ subject, body });
    const href = `mailto:${encodeURIComponent(to)}?${params.toString().replace(/\+/g, "%20")}`;
    // Open in same tab so iOS / Android route to the default mail app.
    window.location.href = href;
  };

  // Quick-pick presets for common date ranges.
  const setPreset = (key) => {
    if (key === "30") {
      setStartDate(daysAgoISO(30));
      setEndDate(todayISO());
    } else if (key === "90") {
      setStartDate(daysAgoISO(90));
      setEndDate(todayISO());
    } else if (key === "ytd") {
      const now = new Date();
      setStartDate(`${now.getFullYear()}-01-01`);
      setEndDate(todayISO());
    } else if (key === "12mo") {
      setStartDate(daysAgoISO(365));
      setEndDate(todayISO());
    }
  };

  return (
    <div className="space-y-8" data-testid="court-bundle-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Records</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 inline-flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-[#116B6E]" strokeWidth={1.5} />
          Court Bundle
        </h1>
        <p className="text-[#6F828A] mt-2 text-base max-w-2xl">
          Generate a single PDF that consolidates messages, custody history, expenses, journal
          entries, documents and audit log for a chosen date range. Each bundle includes a
          tamper-evident fingerprint and a QR code for public verification.
        </p>
      </div>

      {/* Step 1: Date range */}
      <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="bundle-date-card">
        <CardHeader className="border-b border-[#D9E1E3] bg-[#FAFCFC]">
          <CardTitle className="font-heading text-lg inline-flex items-center gap-2">
            <span className="w-7 h-7 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center">
              <CalendarIcon className="w-4 h-4" strokeWidth={1.5} />
            </span>
            1. Date range
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="bundle-presets">
            {[
              { k: "30", l: "Last 30 days" },
              { k: "90", l: "Last 90 days" },
              { k: "ytd", l: "Year to date" },
              { k: "12mo", l: "Last 12 months" },
            ].map((p) => (
              <button
                key={p.k}
                type="button"
                onClick={() => setPreset(p.k)}
                className="px-3 py-1.5 text-xs border border-[#D9E1E3] bg-white text-[#2E3A3F] rounded-sm hover:bg-[#EEF3F4] transition-colors"
                data-testid={`bundle-preset-${p.k}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            <div>
              <Label htmlFor="bundle-start">Start date</Label>
              <Input
                id="bundle-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                data-testid="bundle-start-date"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bundle-end">End date</Label>
              <Input
                id="bundle-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={todayISO()}
                data-testid="bundle-end-date"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Children scope */}
      <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="bundle-scope-card">
        <CardHeader className="border-b border-[#D9E1E3] bg-[#FAFCFC]">
          <CardTitle className="font-heading text-lg inline-flex items-center gap-2">
            <span className="w-7 h-7 rounded-sm bg-[#ECE3F3] text-[#4E3877] inline-flex items-center justify-center">
              <Users className="w-4 h-4" strokeWidth={1.5} />
            </span>
            2. Children & scope
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          {loading ? (
            <div className="text-sm text-[#6F828A]">Loading children…</div>
          ) : children.length === 0 ? (
            <div className="flex items-start gap-2 px-3 py-2 border border-[#E6CFA2] bg-[#FDF4E2] text-[#5C3A1E] rounded-sm text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
              <span>You don't have access to any children yet — add a child or accept an invite first.</span>
            </div>
          ) : (
            <>
              <RadioGroup value={scopeMode} onValueChange={setScopeMode} className="gap-3" data-testid="bundle-scope-mode">
                <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#D9E1E3] rounded-sm hover:bg-[#F4FAFA] transition-colors">
                  <RadioGroupItem value="per_child" id="scope-per" data-testid="scope-mode-per-child" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-medium text-[#2E3A3F]">One PDF per child</div>
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold text-[#116B6E] bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm px-1.5 py-0.5">
                        Recommended
                      </span>
                    </div>
                    <div className="text-xs text-[#6F828A] mt-0.5">
                      You can tick more than one child below — you'll get one separate PDF for each child selected. Matches how the rest of the app stays per-child, and how solicitors typically file records.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-3 border border-[#D9E1E3] rounded-sm hover:bg-[#F4FAFA] transition-colors">
                  <RadioGroupItem value="all" id="scope-all" data-testid="scope-mode-all" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#2E3A3F]">Combined bundle</div>
                    <div className="text-xs text-[#6F828A] mt-0.5">
                      One PDF covering every selected child. Best for an overall picture.
                    </div>
                  </div>
                </label>
              </RadioGroup>

              {/* iter 126.cb-relationship — Optional parent-pair
                  scope. When set, the bundle is narrowed to the
                  relationship's children and the label is stamped
                  into the cover page + fingerprint. Only rendered
                  when the household has ≥ 1 relationship (i.e. after
                  bootstrap has minted a row for the primary parent
                  + placeholder or real co-parent). */}
              {relationships.length > 0 && (
                <div className="pt-1" data-testid="bundle-relationship-scope-wrap">
                  <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-2 font-medium">
                    Parent relationship
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="bundle-relationship-list">
                    <button
                      type="button"
                      onClick={() => setRelationshipId("")}
                      className={`px-3 py-1.5 border rounded-sm text-sm transition-colors ${
                        !relationshipId
                          ? "bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E]"
                          : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"
                      }`}
                      data-testid="bundle-relationship-all"
                    >
                      All relationships
                    </button>
                    {relationships.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRelationshipId(r.id)}
                        className={`px-3 py-1.5 border rounded-sm text-sm transition-colors ${
                          relationshipId === r.id
                            ? "bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E]"
                            : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"
                        }`}
                        data-testid={`bundle-relationship-${r.id}`}
                      >
                        {r.label || `${r.parent_a_name || "Parent A"} ↔ ${r.parent_b_name || "Parent B"}`}
                      </button>
                    ))}
                  </div>
                  {relationshipId && (
                    <div className="text-[11px] text-[#6F828A] mt-2" data-testid="bundle-relationship-hint">
                      Bundle scope narrowed to this pair. Child selection below is limited to their children.
                    </div>
                  )}
                </div>
              )}

              <div className="pt-1">
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-2 font-medium">
                  Children to include
                </div>
                {/* v77.52 — Explicit prompt above the selector. Court
                    bundles can be highly sensitive; the user must
                    consciously pick which child(ren) the bundle
                    relates to rather than relying on a silent default. */}
                <div
                  className="text-sm text-[#2E3A3F] mb-3 leading-relaxed"
                  data-testid="bundle-children-prompt"
                >
                  Please select the child or children this court bundle relates to.
                </div>
                <div className="flex flex-wrap gap-2" data-testid="bundle-children-list">
                  {(relationshipId
                    ? children.filter((c) => {
                        const r = relationships.find((x) => x.id === relationshipId);
                        const rk = (r?.children || []).map((k) => k.id);
                        return !rk.length || rk.includes(c.id);
                      })
                    : children
                  ).map((c) => {
                    const on = selectedChildIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleChild(c.id)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-sm text-sm transition-colors ${
                          on
                            ? "bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E]"
                            : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"
                        }`}
                        data-testid={`bundle-child-${c.id}`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                        {c.name}
                      </button>
                    );
                  })}
                </div>
                {selectedChildIds.length === 0 && (
                  <div className="text-xs text-[#C0291D] mt-2" data-testid="bundle-no-children-warning">
                    Please select at least one child before exporting your court bundle.
                  </div>
                )}

                {/* v77.38/v77.39 — Last exported summary with Stale badge.
                    Shows every accessible child plus their most-recent
                    bundle date / range. A child is "Stale" when their
                    latest bundle is older than 90 days OR they have
                    never been exported. Always rendered (even when no
                    bundles exist) so never-exported children get a
                    visible Stale flag to act on. */}
                {children.length > 0 && (() => {
                  const STALE_DAYS = 90;
                  const staleCount = children.filter((c) => {
                    const last = recentByChild[c.id];
                    if (!last?.exported_at) return true;
                    return (Date.now() - new Date(last.exported_at).getTime()) / 86400000 > STALE_DAYS;
                  }).length;
                  return (
                  <div className="mt-4 pt-4 border-t border-[#EEF3F4]" data-testid="last-exported-section">
                    <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-2 font-medium flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.6} /> Last exported
                      </span>
                      {staleCount > 0 && (
                        <span
                          className="inline-flex items-center text-[10px] normal-case tracking-normal font-medium text-[#5C3A1E] bg-[#FBF0DC] border border-[#E8C29A] rounded-sm px-2 py-0.5"
                          data-testid="last-exported-stale-count"
                        >
                          {staleCount} stale
                        </span>
                      )}
                    </div>
                    <ul className="divide-y divide-[#EEF3F4] border border-[#EEF3F4] rounded-sm bg-[#FAFBFB]">
                      {children.map((c) => {
                        const last = recentByChild[c.id];
                        const exportedAt = last?.exported_at ? new Date(last.exported_at) : null;
                        // v77.39 — Stale-records badge. Soft amber when the
                        // latest bundle is older than 90 days OR the child
                        // has never been exported. Helps mediators / primary
                        // parents catch records that have drifted out-of-
                        // date before a court date sneaks up.
                        const STALE_DAYS = 90;
                        const isStale = exportedAt
                          ? (Date.now() - exportedAt.getTime()) / 86400000 > STALE_DAYS
                          : true;
                        return (
                          <li
                            key={c.id}
                            className="px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 text-sm"
                            data-testid={`last-exported-row-${c.id}`}
                          >
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                              <span className="font-medium text-[#2E3A3F] truncate">{c.name}</span>
                              {isStale && (
                                <span
                                  className="inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold text-[#5C3A1E] bg-[#FBF0DC] border border-[#E8C29A] rounded-sm px-1.5 py-0.5"
                                  title={exportedAt
                                    ? `Last exported over ${STALE_DAYS} days ago`
                                    : "Never exported"}
                                  data-testid={`last-exported-stale-${c.id}`}
                                >
                                  Stale
                                </span>
                              )}
                            </div>
                            {last && exportedAt ? (
                              <div className="text-xs text-[#6F828A] sm:text-right flex-shrink-0 pl-4 sm:pl-0">
                                <span className="font-mono-data text-[#2E3A3F]">{exportedAt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
                                <span className="opacity-70"> · {last.start_date} → {last.end_date}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[#6F828A] italic flex-shrink-0 pl-4 sm:pl-0">Never exported</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  );
                })()}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Sections */}
      <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="bundle-sections-card">
        <CardHeader className="border-b border-[#D9E1E3] bg-[#FAFCFC] flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg inline-flex items-center gap-2">
            <span className="w-7 h-7 rounded-sm bg-[#FBF0DC] text-[#6F4E1E] inline-flex items-center justify-center">
              <FileDown className="w-4 h-4" strokeWidth={1.5} />
            </span>
            3. Sections
          </CardTitle>
          <button
            type="button"
            onClick={() => setAllSections(!allSelected)}
            className="text-xs text-[#116B6E] hover:underline"
            data-testid="bundle-toggle-all-sections"
          >
            {allSelected ? "Clear all" : "Select all"}
          </button>
        </CardHeader>
        <CardContent className="p-5">
          <div className="space-y-3">
            {SECTIONS.map((s) => (
              <div key={s.key}>
                <label
                  className="flex items-start gap-3 cursor-pointer p-3 border border-[#D9E1E3] rounded-sm hover:bg-[#F4FAFA] transition-colors"
                  data-testid={`bundle-section-${s.key}`}
                >
                  <Checkbox
                    checked={!!selectedSections[s.key]}
                    onCheckedChange={() => toggleSection(s.key)}
                    data-testid={`bundle-section-${s.key}-checkbox`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#2E3A3F]">{s.label}</div>
                    <div className="text-xs text-[#6F828A] mt-0.5 leading-relaxed">{s.hint}</div>
                  </div>
                </label>
                {/* Iter 122.cb-cv — Inline Contact Visits options
                    panel. Only renders when the section is enabled so
                    the form stays calm by default. */}
                {s.key === "contact_visits" && selectedSections.contact_visits && (
                  <div className="mt-2 ml-8 mr-1 p-4 bg-[#F4FAFA] border border-[#A7D8DA] rounded-sm space-y-4"
                       data-testid="bundle-cv-options">
                    {/* Filter mode */}
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-semibold mb-2">Which visits to include</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          ["all",     "All in date range"],
                          ["filter",  "Filtered"],
                        ].map(([k, label]) => (
                          <button key={k} type="button"
                            onClick={() => setCvOption("mode", k)}
                            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                              cvOptions.mode === k
                                ? "bg-[#116B6E] text-white border-[#116B6E]"
                                : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#EEF3F4]"
                            }`}
                            data-testid={`bundle-cv-mode-${k}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                      {cvOptions.mode === "filter" && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {[
                            ["disputed",              "Disputed only"],
                            ["verified",              "Verified only"],
                            ["supervised",            "Supervised only"],
                            ["cancelled",             "Cancelled only"],
                            ["awaiting_verification", "Awaiting verification"],
                            ["completed",             "Completed"],
                          ].map(([k, label]) => (
                            <button key={k} type="button"
                              onClick={() => setCvOption("status_filter", cvOptions.status_filter === k ? null : k)}
                              className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                                cvOptions.status_filter === k
                                  ? "bg-[#0F5658] text-white border-[#0F5658]"
                                  : "bg-white text-[#48595E] border-[#D9E1E3] hover:bg-[#EEF3F4]"
                              }`}
                              data-testid={`bundle-cv-filter-${k}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Per-visit content toggles */}
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-semibold mb-2">Per-visit content</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {[
                          ["include_times",         "Start / end times"],
                          ["include_duration",      "Duration"],
                          ["include_venue",         "Venue"],
                          ["include_gps",           "GPS readings"],
                          ["include_participants",  "Participants"],
                          ["include_outcome",       "Outcome"],
                          ["include_verification",  "Verification"],
                          ["include_notes",         "Shared notes"],
                          ["include_private_notes", "Private notes"],
                          ["include_attachments",   "Attachments"],
                          ["include_observations",  "Third-party observations"],
                          ["include_timeline",      "Audit timeline"],
                        ].map(([k, label]) => (
                          <label key={k} className="flex items-center gap-1.5 text-xs text-[#2E3A3F] cursor-pointer">
                            <Checkbox
                              checked={!!cvOptions[k]}
                              onCheckedChange={(v) => setCvOption(k, !!v)}
                              data-testid={`bundle-cv-${k.replace(/_/g, "-")}`}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Presentation */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs text-[#2E3A3F] cursor-pointer">
                        <Checkbox
                          checked={!!cvOptions.summary_only}
                          onCheckedChange={(v) => setCvOption("summary_only", !!v)}
                          data-testid="bundle-cv-summary-only"
                        />
                        <span>Summary only (one line per visit, no full record)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Iter 122.cb-cv — Bundle Preview. Shows live counts + page
          estimate before the user clicks Generate so they can adjust
          filters with confidence. The preview hydrates from the same
          /exports/court-bundle/estimate endpoint already used by the
          confirmation flow. */}
      <Card className="border-[#A7D8DA] bg-[#F4FAFA] rounded-md shadow-none" data-testid="bundle-preview-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-sm bg-[#D6F0EF] text-[#0F5658] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileDown className="w-4 h-4" strokeWidth={1.6} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-semibold">
                Bundle preview
              </div>
              {previewLoading ? (
                <div className="text-xs text-[#6F828A] mt-1.5 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
                  Calculating…
                </div>
              ) : !preview ? (
                <div className="text-xs text-[#6F828A] mt-1.5">
                  Pick a date range, at least one child and at least one section to see a preview.
                </div>
              ) : (
                <>
                  <div className="text-xs text-[#48595E] mt-1.5 leading-relaxed">
                    {(() => {
                      const counts = preview?.counts || {};
                      const observations_count = preview?.observations_count;
                      const order = [
                        ["contact_visits",     "Contact Visit"],
                        ["messages",           "Message"],
                        ["custody",            "Custody record"],
                        ["expenses",           "Expense"],
                        ["upcoming_expenses",  "Upcoming expense"],
                        ["maintenance",        "Maintenance record"],
                        ["journal",            "Journal entry"],
                        ["documents",          "Document"],
                        ["profiles",           "Child profile"],
                        ["audit",              "Audit row"],
                      ];
                      const parts = order
                        .filter(([k]) => counts[k] > 0)
                        .map(([k, singular]) => {
                          const n = counts[k];
                          const plural = singular === "Document" ? "Documents"
                                       : singular.endsWith("y") ? singular.slice(0, -1) + "ies"
                                       : singular + "s";
                          return `${n} ${n === 1 ? singular : plural}`;
                        });
                      if (observations_count > 0) {
                        parts.splice(1, 0, `${observations_count} third-party observation${observations_count === 1 ? "" : "s"}`);
                      }
                      return parts.length ? parts.join(" · ") : "No records in the selected window.";
                    })()}
                  </div>
                  {preview.est_total_pages && (
                    <div className="text-xs text-[#0F5658] mt-1.5 font-medium">
                      Estimated ~{preview.est_total_pages} page{preview.est_total_pages === 1 ? "" : "s"}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action */}
      <div className="space-y-3">
        {/* v77.99.98 — Email-on-ready toggle. Surfaces above the
            Generate button so users see it before clicking. Survives
            page reloads via localStorage. */}
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="bundle-notify-card">
          <CardContent className="p-4 flex items-start gap-3">
            <span className="w-8 h-8 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mail className="w-4 h-4" strokeWidth={1.6} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2E3A3F]">
                    Email me when ready
                  </div>
                  <div className="text-xs text-[#6F828A] mt-0.5 leading-relaxed">
                    Get one combined email at {user?.email ? <strong className="text-[#2E3A3F]">{user.email}</strong> : "your account email"} once every bundle has finished generating. You can safely close this tab — downloads stay available from your{" "}
                    <Link to="/app/court-bundle/history" className="text-[#116B6E] underline underline-offset-2 hover:text-[#0F5658]" data-testid="bundle-notify-history-link">
                      bundle history
                    </Link>{" "}
                    for 30 days.
                  </div>
                </div>
                <Switch
                  checked={notifyEmailOnReady}
                  onCheckedChange={persistNotifyEmail}
                  data-testid="bundle-notify-email-toggle"
                  className="flex-shrink-0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="text-xs text-[#6F828A] inline-flex items-start gap-2 max-w-xl leading-relaxed">
            <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
            <span>
              Each bundle is fingerprinted (SHA-256 prefix) and includes a QR code linking to a public
              verification page. Anyone receiving the PDF can confirm it was issued through CoParent
              Console without needing an account.
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Button
              type="button"
              variant="outline"
              asChild
              className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]"
              data-testid="bundle-history-link-button"
            >
              <Link to="/app/court-bundle/history">
                <HistoryIcon className="w-4 h-4 mr-2" strokeWidth={1.6} />
                History
              </Link>
            </Button>
            <Button
              type="button"
              onClick={() => onExport()}
              onMouseEnter={() => {
                // Iter 122.trial-pivot — Hovering the primary "Generate"
                // CTA is the strongest possible export-intent signal —
                // morph the topbar pill so trial users see the path
                // before they click and hit a 402.
                import("@/lib/exportIntent").then(({ broadcastExportIntent }) =>
                  broadcastExportIntent({ feature: "Court Bundle", ttl_ms: 10000 })
                );
              }}
              disabled={exporting || loading || children.length === 0 || selectedChildIds.length === 0}
              className="bg-[#116B6E] hover:bg-[#0F5658] text-white whitespace-nowrap"
              data-testid="bundle-export-button"
              size="lg"
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.8} /> Generating…</>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" strokeWidth={1.6} />
                  Generate court bundle{scopeMode === "per_child" && selectedChildIds.length > 1 ? "s" : ""}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* v77.99.29 — Progress card. Renders while an export is in
          flight so the user knows the job is alive — the previous
          "Generating…" spinner gave no signal during long
          ReportLab builds and felt broken on mobile. The stage label
          is driven by `progress.stage` (mapped via STAGE_LABELS) and
          the bar by `progress.progress_pct`. Both come from the
          backend's `_run_court_bundle_job` ticker. */}
      {exporting && progress && (
        <Card className="border-[#A6D7D9] rounded-md shadow-none bg-[#F2FAFA]" data-testid="bundle-progress-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-[#116B6E] animate-spin flex-shrink-0" strokeWidth={1.8} />
              <div className="min-w-0">
                <div className="font-heading text-base font-medium text-[#0B2E32]">
                  {progress.target_count > 1
                    ? `Bundle ${progress.target_idx} of ${progress.target_count} · ${progress.label}`
                    : `Generating bundle · ${progress.label}`}
                </div>
                <div className="text-sm text-[#3A4548] mt-0.5" data-testid="bundle-progress-stage-label">
                  {STAGE_LABELS[progress.stage] || STAGE_LABELS.running}
                </div>
              </div>
              <div className="ml-auto font-mono-data text-sm text-[#116B6E] tabular-nums" data-testid="bundle-progress-pct">
                {progress.progress_pct || 0}%
              </div>
            </div>
            <div className="h-2 rounded-full bg-[#D6F0EF] overflow-hidden">
              <div
                className="h-full bg-[#116B6E] transition-all duration-700"
                style={{ width: `${Math.max(2, progress.progress_pct || 0)}%` }}
              />
            </div>
            <div className="text-xs text-[#6F828A]">
              {/* v77.99.161 (iter 139.cb-honest-progress) — Honest
                  copy. Large bundles can take several minutes
                  depending on receipt count and attachment size.
                  "Email me when ready" is enabled by default so the
                  user can safely close the tab. */}
              You can safely close this tab — the bundle keeps building in the background. If you enabled "Email me when ready", you'll get a notification the moment every selected bundle is finished.
            </div>
          </CardContent>
        </Card>
      )}

      {/* v77.99.29 — Failure card. Replaces the generic "Failed to
          fetch" toast with calm, actionable guidance. Form state is
          preserved (React state) so the user doesn't lose their
          selection. Inline buttons offer the safer alternatives. */}
      {!exporting && lastFailure && (
        <Card className="border-[#E6BAB6] rounded-md shadow-none bg-[#FBEFEE]" data-testid="bundle-failure-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-sm bg-[#F7D6D2] text-[#A93A2F] inline-flex items-center justify-center flex-shrink-0">
                <FileWarning className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <div className="min-w-0">
                <div className="font-heading text-base font-semibold text-[#5C1F19]">
                  {lastFailure.headline}
                </div>
                <div className="text-sm text-[#4A2520] mt-1">
                  {lastFailure.detail}
                </div>
                <div className="text-xs text-[#7A4B45] mt-2">
                  Your records and selections are still saved above — tap Retry to run this export again against the same frozen snapshot.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* v77.99.163 (iter 141.cb-retry-always) — "Retry this
                  export" is now the ALWAYS-primary action, regardless
                  of whether a `failed_job_id` was captured. When the
                  initial POST failed (e.g. "Failed to fetch" during a
                  deploy window), there is no job_id to point at —
                  but the user's form state is still saved, so we can
                  simply re-run `runExport()` with the identical
                  values. When a job DID exist, we use the retry-
                  snapshot endpoint for a true "same frozen scope"
                  rerun that preserves the audit chain. The user
                  never sees "Shorten the date range" as a primary
                  recovery path. */}
              <Button
                size="sm"
                className="bg-[#A93A2F] text-white hover:bg-[#8F2E24]"
                onClick={() => {
                  if (lastFailure.failed_job_id) {
                    retryFailedBundle();
                  } else {
                    setLastFailure(null);
                    runExport();
                  }
                }}
                data-testid="bundle-failure-action-retry"
              >
                Retry this export
              </Button>
              {/* v77.99.157 — "Try as one PDF per child" demoted to
                  a secondary suggestion. A combined bundle is a
                  legitimate output; splitting is not the primary
                  recovery path. */}
              {selectedChildIds.length > 1 && scopeMode !== "per_child" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#A93A2F] text-[#A93A2F] hover:bg-[#F7D6D2]"
                  onClick={() => runWithRecommendation("split_per_child")}
                  data-testid="bundle-failure-action-split"
                >
                  Or one PDF per child
                </Button>
              )}
              {/* v77.99.163 (iter 141.cb-retry-always) — "Shorten to
                  last 90 days" and "Try without documents / audit
                  log" have been removed from the failure card
                  entirely. The user explicitly said we should not
                  encourage parents to drop records to make a bundle
                  pass. Retry is the correct recovery path; the
                  server will clearly surface any genuinely missing
                  attachments via the manifest. */}
              {false && !lastFailure.failed_job_id && (selectedSections.documents || selectedSections.audit) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#A93A2F] text-[#A93A2F] hover:bg-[#F7D6D2]"
                  onClick={() => runWithRecommendation("drop_attachments")}
                  data-testid="bundle-failure-action-drop-attachments"
                >
                  Try without documents / audit log
                </Button>
              )}
              {false && !lastFailure.failed_job_id && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#A93A2F] text-[#A93A2F] hover:bg-[#F7D6D2]"
                  onClick={() => runWithRecommendation("shorten_range")}
                  data-testid="bundle-failure-action-shorten"
                >
                  Shorten to last 90 days
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-[#A93A2F] text-[#A93A2F] hover:bg-[#F7D6D2]"
                onClick={async () => {
                  // v77.99.115n — One-click diagnostic copy. Bundles
                  // the headline + raw backend error + the user's
                  // form selections + a timestamp so the support
                  // channel can paste a single message and we have
                  // everything needed to pin the failure to a row
                  // in `export_jobs`.
                  const payload = [
                    `Court bundle failure`,
                    `Time:      ${lastFailure.at || new Date().toISOString()}`,
                    `Headline:  ${lastFailure.headline || "(none)"}`,
                    `Detail:    ${lastFailure.detail || lastFailure.raw || "(none)"}`,
                    `Range:     ${startDate} → ${endDate}`,
                    `Children:  ${selectedChildIds.length} selected${scopeMode === "per_child" ? " (one PDF each)" : " (combined)"}`,
                    `Sections:  ${Object.entries(selectedSections).filter(([, v]) => v).map(([k]) => k).join(", ") || "(none)"}`,
                  ].join("\n");
                  try {
                    await navigator.clipboard.writeText(payload);
                    toast.success("Error detail copied — paste into your support reply.");
                  } catch (err) {
                    // Clipboard API can fail on iOS Safari in some
                    // contexts. Fall back to a temporary textarea +
                    // execCommand so the user is never stuck.
                    const ta = document.createElement("textarea");
                    ta.value = payload;
                    ta.style.position = "fixed";
                    ta.style.opacity = "0";
                    document.body.appendChild(ta);
                    ta.select();
                    try {
                      document.execCommand("copy");
                      toast.success("Error detail copied — paste into your support reply.");
                    } catch (err2) {
                      void err; void err2;
                      toast.error("Couldn't auto-copy — please screenshot this card.");
                    }
                    ta.remove();
                  }
                }}
                data-testid="bundle-failure-copy-detail"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy error detail
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-[#7A4B45] hover:bg-[#F7D6D2]"
                onClick={() => setLastFailure(null)}
                data-testid="bundle-failure-dismiss"
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* v77.99.29 — Pre-flight estimate modal. Renders inline as a
          card to avoid any layered-dialog flakiness on mobile. Only
          appears when the server estimates the combined bundle is
          likely too large to ship reliably; offers concrete
          recommendations and lets the user proceed anyway if they
          accept the risk. */}
      {pendingEstimate && !exporting && (
        <Card className="border-[#E6CFA2] rounded-md shadow-none bg-[#FFF8E8]" data-testid="bundle-estimate-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-sm bg-[#FFE5B4] text-[#8F6B1F] inline-flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <div className="min-w-0">
                <div className="font-heading text-base font-semibold text-[#5C4516]">
                  {pendingEstimate.estimate.warning_level === "high"
                    ? "This bundle is very large."
                    : "This is a sizeable bundle."}
                </div>
                <div className="text-sm text-[#5C4516] mt-1">
                  Combined export for {pendingEstimate.estimate.scope_child_count} children — roughly{" "}
                  <strong>{formatBytes(pendingEstimate.estimate.est_bytes)}</strong> and around{" "}
                  <strong>{pendingEstimate.estimate.est_seconds}s</strong> to generate.{" "}
                  Mobile connections can drop very large downloads. Safer alternatives below — pick one or carry on as planned.
                </div>
                <div className="text-xs text-[#7A6320] mt-2 font-mono-data">
                  {Object.entries(pendingEstimate.estimate.counts).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}
                  {pendingEstimate.estimate.attachment_count > 0 ? `  ·  receipts: ${pendingEstimate.estimate.attachment_count}` : ""}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {pendingEstimate.estimate.recommendations.map((r) => (
                <Button
                  key={r.id}
                  size="sm"
                  variant="outline"
                  className="border-[#8F6B1F] text-[#5C4516] hover:bg-[#FFE5B4]"
                  onClick={() => {
                    setPendingEstimate(null);
                    runWithRecommendation(r.id);
                  }}
                  title={r.reason}
                  data-testid={`bundle-estimate-action-${r.id}`}
                >
                  {r.label}
                </Button>
              ))}
              <Button
                size="sm"
                className="bg-[#116B6E] text-white hover:bg-[#0F5658]"
                onClick={() => {
                  setPendingEstimate(null);
                  onExport({ skipEstimate: true });
                }}
                data-testid="bundle-estimate-action-continue"
              >
                Continue with combined export
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-[#5C4516] hover:bg-[#FFE5B4]"
                onClick={() => setPendingEstimate(null)}
                data-testid="bundle-estimate-action-cancel"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* v77.48 — Share section. Surfaces only after at least one
          bundle has been generated this session. Mobile-first: native
          Web Share API takes one tap to email / Drive / WhatsApp /
          AirDrop. Desktop falls back to the solicitor mailto + an
          obvious "Re-download" path. */}
      {generatedBundles.length > 0 && (
        <Card className="border-[#A6D7D9] rounded-md shadow-none bg-[#F2FAFA]" data-testid="bundle-share-card">
          <CardHeader className="border-b border-[#A6D7D9]">
            <CardTitle className="font-heading text-lg inline-flex items-center gap-2">
              <span className="w-7 h-7 rounded-sm bg-[#D6F0EF] text-[#116B6E] inline-flex items-center justify-center">
                <Share2 className="w-4 h-4" strokeWidth={1.5} />
              </span>
              Send your bundle
            </CardTitle>
            <p className="text-xs text-[#3F595B] mt-1.5">
              You just generated {generatedBundles.length} PDF{generatedBundles.length === 1 ? "" : "s"}. Share {generatedBundles.length === 1 ? "it" : "them"} with your solicitor or mediator without leaving the app.
            </p>
          </CardHeader>
          <CardContent className="space-y-4 pt-5">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1 min-w-0">
                <Label htmlFor="solicitor-email" className="text-xs uppercase tracking-[0.1em] text-[#6F828A] font-medium">
                  Solicitor / mediator email
                </Label>
                <Input
                  id="solicitor-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="solicitor@example.com"
                  value={solicitorEmail}
                  onChange={(e) => persistSolicitorEmail(e.target.value)}
                  className="mt-1.5"
                  data-testid="solicitor-email-input"
                />
                <p className="text-[11px] text-[#6F828A] mt-1">
                  Saved on this device only. Used to prefill the email subject + body.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {canShareFiles && (
                <Button
                  type="button"
                  onClick={() => shareNative(generatedBundles)}
                  className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
                  data-testid="bundle-share-native-button"
                >
                  <Share2 className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
                  Share via…
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => emailToSolicitor(generatedBundles)}
                className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]"
                data-testid="bundle-share-email-button"
              >
                <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
                {solicitorEmail ? `Email to ${solicitorEmail.split("@")[0]}…` : "Email to solicitor…"}
              </Button>
            </div>

            <ul className="border border-[#A6D7D9] rounded-sm bg-white divide-y divide-[#D6F0EF]" data-testid="bundle-share-list">
              {generatedBundles.map((b) => (
                <li
                  key={b.id}
                  className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
                  data-testid={`bundle-share-item-${b.label}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[#2E3A3F] truncate">{b.label}</div>
                    <div className="text-[11px] text-[#6F828A] truncate font-mono-data" title={b.filename}>
                      {b.filename}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => reDownload(b)}
                    className="self-end sm:self-auto text-[#116B6E] hover:bg-[#D6F0EF]"
                    data-testid={`bundle-share-redownload-${b.label}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" strokeWidth={1.6} />
                    Re-download
                  </Button>
                </li>
              ))}
            </ul>

            {!canShareFiles && (
              <p className="text-[11px] text-[#6F828A] leading-relaxed">
                Tip: on iOS and Android you can share PDFs straight from this page — open CoParent Console in Safari or Chrome instead of an in-app browser. On desktop, use the email button above to draft a message, then attach the file you downloaded.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
